import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Grid2 as Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ExtensionIcon from '@mui/icons-material/Extension';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import Page from '../Page';
import { varFadeInUp, MotionInView } from '../animate';
import TryLiveDemoButton from '../TryLiveDemoButton';
import LandingDashboardMock from '../_external-pages/landing/LandingDashboardMock';
import { PATH_PAGE } from 'routes/paths';

// ----------------------------------------------------------------------

const RootStyle = styled(Page)(({ theme }) => ({
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(15),
  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(11),
  },
}));

const SectionStyle = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(6),
}));

const CardStyle = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: theme.customShadows.z16,
  marginBottom: theme.spacing(4),
}));

const StepCardStyle = styled('div')(({ theme }) => ({
  position: 'relative',
  height: '100%',
  overflow: 'hidden',
  padding: theme.spacing(4),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: 'transform 0.25s, box-shadow 0.25s',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: theme.shadows[12],
  },
}));

// Steps that answer the page's real question: how does raw store data become trustworthy analytics.
const STEPS = [
  {
    icon: LinkRoundedIcon,
    title: 'Connect your stores',
    body:
      'Point your App Store, Google Play and Paddle webhooks at Mobeat. No SDK rewrite — Mobeat listens to the ' +
      'server notifications you already emit.',
  },
  {
    icon: TuneRoundedIcon,
    title: 'Mobeat makes sense of every event',
    body:
      'Activations, trials, renewals, offers, cancellations and refunds are normalized into one model and matched ' +
      'to the right user and store — refunds netted, real proceeds captured, users deduplicated.',
  },
  {
    icon: InsightsRoundedIcon,
    title: 'See revenue you can trust',
    body:
      'MRR, churn, LTV, retention cohorts and Net ROAS update in real time — with the definitions that match how ' +
      'each store actually reports.',
  },
];

// Curated map of the analytics suite grouped by theme — reflects the real chart depth (conversion,
// cohorts, reactivation, refund-accurate churn) without dumping every individual chart name.
const ANALYTICS_GROUPS = [
  {
    title: 'Revenue & LTV',
    items: [
      ['MRR & ARR', 'Recurring revenue over time, gross and net of refunds'],
      ['Revenue by source', 'New vs renewal revenue, split by country, product and store'],
      ['Lifetime value', 'LTV per paying user and across your whole user base'],
    ],
  },
  {
    title: 'Conversion',
    items: [
      ['Initial & trial conversion', 'How many installs start paying, and how many trials convert'],
      ['Offer & intro conversion', 'Conversion on promotional and intro-priced offers'],
      ['Reactivation', 'Lapsed subscribers who come back — tracked separately from new'],
    ],
  },
  {
    title: 'Cohort analysis',
    items: [
      ['Cohorted conversion', 'Initial, trial, intro and reactivation conversion by first-seen cohort'],
      ['Cohort Explorer', 'Multi-metric retention and revenue by cohort, with payback / recoup'],
      ['Retention curves', 'How each cohort retains over time'],
    ],
  },
  {
    title: 'Trials & subscriptions',
    items: [
      ['Active & new trials', 'Trials in progress, new trials, and trial movement'],
      ['Active subscribers', 'Subscriber counts and month-over-month movement'],
      ['Status & events', 'Subscription status, auto-renew status and a full events timeline'],
    ],
  },
  {
    title: 'Churn & refunds',
    items: [
      ['Churn & refund rate', 'Cancellations and refunds, with refunds netted correctly'],
      ['Refund detail', 'App Store refund requests and Play Store cancel reasons'],
      ['Cancellation timing', 'When in the lifecycle subscribers cancel'],
    ],
  },
  {
    title: 'Customers',
    items: [['Top spenders', 'Your highest-value users, by country and acquisition source']],
  },
];

// Consistent, on-brand header for each feature accordion (icon chip + title).
function SummaryHead({ icon: Icon, title }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'primary.main',
          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
        }}
      >
        <Icon />
      </Box>
      <Typography variant='h5'>{title}</Typography>
    </Box>
  );
}

SummaryHead.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
};

// ----------------------------------------------------------------------

export default function MobeatPlatformGuide() {
  const [expanded, setExpanded] = useState('analytics');

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <RootStyle title='Mobeat Platform Guide | Mobeat'>
      <Container maxWidth='lg'>
        {/* Hero */}
        <MotionInView variants={varFadeInUp}>
          <Box sx={{ mb: { xs: 8, md: 10 } }}>
            <Typography
              variant='overline'
              sx={{ display: 'block', textAlign: 'center', color: 'primary.main', mb: 2 }}
            >
              Platform guide
            </Typography>
            <Typography variant='h2' sx={{ textAlign: 'center', fontWeight: 'bold', mb: 3 }}>
              How Mobeat works
            </Typography>
            <Typography
              variant='h5'
              sx={{
                textAlign: 'center',
                color: 'text.secondary',
                fontWeight: 'regular',
                maxWidth: 760,
                mx: 'auto',
                mb: { xs: 6, md: 8 },
              }}
            >
              From store webhooks to revenue you can trust — see how Mobeat turns raw subscription events into accurate
              analytics.
            </Typography>
            <Box sx={{ maxWidth: 720, mx: 'auto' }}>
              <LandingDashboardMock />
            </Box>
          </Box>
        </MotionInView>

        {/* How it works — 3 steps */}
        <SectionStyle>
          <MotionInView variants={varFadeInUp}>
            <Grid container spacing={3}>
              {STEPS.map((step, index) => {
                const StepIcon = step.icon;
                return (
                  <Grid key={step.title} size={{ xs: 12, md: 4 }}>
                    <StepCardStyle>
                      <Typography
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 16,
                          fontSize: 72,
                          fontWeight: 800,
                          lineHeight: 1,
                          color: (t) => alpha(t.palette.text.primary, 0.05),
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          mb: 2.5,
                          borderRadius: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'primary.main',
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                        }}
                      >
                        <StepIcon />
                      </Box>
                      <Typography variant='h5' sx={{ mb: 1 }}>
                        {step.title}
                      </Typography>
                      <Typography variant='body1' sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                        {step.body}
                      </Typography>
                    </StepCardStyle>
                  </Grid>
                );
              })}
            </Grid>
          </MotionInView>
        </SectionStyle>

        <SectionStyle>
          <Typography variant='h4' sx={{ mb: 3 }}>
            What is Mobeat?
          </Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
            Mobeat is a subscription analytics and revenue management platform for mobile apps and subscription
            businesses. With direct integrations to the App Store, Google Play and Paddle — plus popular Mobile
            Measurement Partners (MMPs) — it gives you one accurate view of your revenue, cohorts and LTV, so you can
            make data-driven decisions to grow.
          </Typography>
        </SectionStyle>

        <SectionStyle>
          <Typography variant='h4' sx={{ mb: 1 }}>
            Explore the platform
          </Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary', mb: 3 }}>
            A closer look at what each part of Mobeat does.
          </Typography>

          <Accordion expanded={expanded === 'analytics'} onChange={handleChange('analytics')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='analytics-content' id='analytics-header'>
              <SummaryHead icon={BarChartIcon} title='Subscription Analytics' />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant='body1' paragraph>
                  Mobeat turns your store events into a full analytics suite — from headline revenue down to
                  cohorted conversion, reactivation and refund-accurate churn. A few of the areas you get:
                </Typography>

                <Grid container spacing={2}>
                  {ANALYTICS_GROUPS.map((group) => (
                    <Grid key={group.title} size={{ xs: 12, md: 6 }}>
                      <CardStyle sx={{ height: '100%', mb: 0 }}>
                        <CardContent>
                          <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                            {group.title}
                          </Typography>
                          <List dense disablePadding>
                            {group.items.map(([primary, secondary]) => (
                              <ListItem key={primary} disableGutters alignItems='flex-start'>
                                <ListItemIcon sx={{ minWidth: 34, mt: 0.5 }}>
                                  <CheckCircleOutlineIcon color='primary' fontSize='small' />
                                </ListItemIcon>
                                <ListItemText primary={primary} secondary={secondary} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </CardStyle>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'appstore'} onChange={handleChange('appstore')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls='appstore-content' id='appstore-header'>
              <SummaryHead icon={ShoppingCartIcon} title='Store Integrations' />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant='body1' paragraph>
                  Mobeat seamlessly integrates with both Apple App Store and Google Play Store to provide real-time
                  transaction processing and subscription management.
                </Typography>

                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Apple App Store Integration
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Server-to-Server Notifications'
                              secondary='Process App Store Server Notifications (version 2) for real-time subscription events'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Receipt Validation'
                              secondary='Automatic verification of purchase receipts for security'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='StoreKit Integration'
                              secondary='Support for StoreKit 1 and 2 transactions'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Google Play Store Integration
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Real-time Developer Notifications'
                              secondary='Process Play Store RTDNs for subscription state changes'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Purchase Validation'
                              secondary='Secure verification of Google Play transactions'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Subscription Management'
                              secondary='Handle subscription lifecycle events and state changes'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>
                </Grid>

                <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                  Key Benefits
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.neutral', mb: 2 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Automatic Transaction Processing'
                        secondary='All store transactions are automatically processed and recorded'
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Real-time Status Updates'
                        secondary='Immediate updates on subscription states, trials, and renewals'
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Consumption Management'
                        secondary='Track and process consumption requests for consumable products'
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'integrations'} onChange={handleChange('integrations')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='integrations-content'
              id='integrations-header'
            >
              <SummaryHead icon={ExtensionIcon} title='Third-Party Integrations' />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant='body1' paragraph>
                  Mobeat integrates with popular Mobile Measurement Partners (MMPs) and other third-party services to
                  provide a comprehensive view of your app's performance.
                </Typography>

                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Analytics & Attribution Integrations
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Amplitude Integration'
                              secondary='Sync subscription and revenue data with your Amplitude analytics'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='AppsFlyer Integration'
                              secondary='Connect attribution data with subscription performance'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Custom Analytics Webhooks'
                              secondary='Send subscription events to any analytics platform'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Data & Communication Integrations
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Data Export Options'
                              secondary='Export subscription and revenue data to your own systems'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Email Service Providers'
                              secondary='Integrate with Mailjet and Mailgun for email communications'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Push Notification Services'
                              secondary='FCM integration for mobile push notifications'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>
                </Grid>

                <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mt: 3, mb: 1 }}>
                  Integration Benefits
                </Typography>
                <Paper sx={{ p: 2, bgcolor: 'background.neutral', mb: 2 }}>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Unified Data View'
                        secondary='Combine attribution, engagement, and revenue data in one place'
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Automated Workflows'
                        secondary='Trigger communications based on subscription events'
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <CheckCircleOutlineIcon color='primary' />
                      </ListItemIcon>
                      <ListItemText
                        primary='Custom Event Tracking'
                        secondary='Track and respond to specific in-app events that affect subscriptions'
                      />
                    </ListItem>
                  </List>
                </Paper>
              </Box>
            </AccordionDetails>
          </Accordion>

          <Accordion expanded={expanded === 'monetization'} onChange={handleChange('monetization')}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='monetization-content'
              id='monetization-header'
            >
              <SummaryHead icon={MonetizationOnIcon} title='Monetization & Engagement' />
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%' }}>
                <Typography variant='body1' paragraph>
                  Mobeat provides tools to help you optimize your monetization strategy and engage with your users at
                  critical moments in their subscription journey.
                </Typography>

                <Grid container spacing={3}>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Email Marketing Capabilities
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Targeted Email Campaigns'
                              secondary='Send emails based on subscription status or user behavior'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='External Template Integration'
                              secondary='Use template IDs from Mailjet and Mailgun for consistent email delivery'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Automated Subscription Emails'
                              secondary='Send welcome, renewal reminder, and churn prevention emails'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      md: 6
                    }}>
                    <CardStyle>
                      <CardContent>
                        <Typography variant='subtitle1' sx={{ fontWeight: 'bold', mb: 1 }}>
                          Push Notifications & In-App Messaging
                        </Typography>
                        <List>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Event-Triggered Notifications'
                              secondary='Send push notifications based on subscription events'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='Re-engagement Campaigns'
                              secondary='Target lapsed subscribers with personalized messages'
                            />
                          </ListItem>
                          <ListItem>
                            <ListItemIcon>
                              <CheckCircleOutlineIcon color='primary' />
                            </ListItemIcon>
                            <ListItemText
                              primary='FCM Integration'
                              secondary='Reliable delivery of push notifications via Firebase Cloud Messaging'
                            />
                          </ListItem>
                        </List>
                      </CardContent>
                    </CardStyle>
                  </Grid>
                </Grid>

                <Typography variant='h6' sx={{ mt: 3, mb: 1 }}>
                  Transaction Management
                </Typography>
                <CardStyle>
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon color='primary' />
                        </ListItemIcon>
                        <ListItemText
                          primary='Consumption Request Tracking'
                          secondary='Monitor and process consumption-based transactions'
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon color='primary' />
                        </ListItemIcon>
                        <ListItemText
                          primary='Transaction History'
                          secondary='View complete transaction history for all users'
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleOutlineIcon color='primary' />
                        </ListItemIcon>
                        <ListItemText
                          primary='Offer Management'
                          secondary='Create and track promotional offers for subscriptions'
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </CardStyle>
              </Box>
            </AccordionDetails>
          </Accordion>
        </SectionStyle>

        {/* Closing CTA */}
        <Box sx={{ textAlign: 'center', mt: { xs: 8, md: 10 } }}>
          <Typography variant='h4' gutterBottom>
            See it on your own data
          </Typography>
          <Typography variant='body1' sx={{ color: 'text.secondary', maxWidth: 560, mx: 'auto', mb: 4 }}>
            Connect your stores and Mobeat starts turning webhooks into accurate revenue analytics — usually within a
            day.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent='center' alignItems='center'>
            <Button component={RouterLink} to={PATH_PAGE.requestDemo} variant='contained' size='large'>
              Request a demo
            </Button>
            <TryLiveDemoButton size='large' variant='outlined' />
          </Stack>
        </Box>
      </Container>
    </RootStyle>
  );
}
